import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  Droplet, Shield, Network, Megaphone, Cpu, Eye, Sprout, ArrowRight, Award, Clock, BookOpen, Check,
} from "lucide-react";

const ICONS = { Droplet, Shield, Network, Megaphone, Cpu, Eye, Sprout };

export default function Tracks() {
  useDocumentMeta({
    title: "Specialty Tracks — APM Fire Alarm Exam Prep",
    description: "Specialty learning tracks: FDNY S-95, F-53, S-12, S-13, BMS & Elevator Recall, Smoke Control & Voice EVAC, Manufacturer Exposure (EST3 / Siemens / Notifier).",
    path: "/tracks",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/tracks");
        setData(d);
      } catch {
        // Track list fetch failed; UI shows empty state
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="tracks-page">
        <div className="overline mb-3"><span className="red-bar" />Specialty Tracks</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-4">
          Beyond NICET.<br />
          <span className="text-red-600">Specialty credentials for working techs.</span>
        </h1>
        <p className="text-zinc-700 max-w-2xl leading-relaxed mb-10">
          Seven additional career-accelerating tracks. Each issues its own completion credential
          when you pass the final exam. All included with your $149 enrollment — no extra cost.
        </p>

        {loading ? (
          <div className="text-zinc-500 font-mono text-sm text-center py-20">Loading tracks...</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5" data-testid="tracks-list">
            {data?.tracks?.map((t) => {
              const Icon = ICONS[t.icon] || BookOpen;
              return (
                <Link
                  key={t.id}
                  to={`/learn/track/${t.id}`}
                  className="card-tech p-6 hover:border-red-600 transition-colors group block"
                  data-testid={`track-card-${t.id}`}
                  style={{ borderTop: `4px solid ${t.color}` }}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="w-12 h-12 grid place-items-center" style={{ background: `${t.color}15`, color: t.color }}>
                      <Icon className="w-6 h-6" strokeWidth={1.8} />
                    </div>
                    {t.final_exam_passed && (
                      <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-[10px] font-mono uppercase tracking-wider font-bold">
                        <Award className="w-3 h-3" /> Credentialed
                      </div>
                    )}
                  </div>

                  <div className="overline mb-1">{t.short_name}</div>
                  <h2 className="font-heading text-xl font-bold tracking-tight mb-2 group-hover:text-red-600 transition-colors">
                    {t.name}
                  </h2>
                  <p className="text-sm text-zinc-700 leading-relaxed mb-4">{t.description}</p>

                  <div className="flex items-center gap-4 text-xs font-mono text-zinc-600 mb-4">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {t.hours} hrs</span>
                    <span className="inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {t.modules_count} modules</span>
                    <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {t.lessons_total} lessons</span>
                  </div>

                  {/* Progress bar */}
                  <div className="bg-zinc-100 h-1.5 mb-2">
                    <div className="h-full" style={{ width: `${t.progress_pct}%`, background: t.color }} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-mono text-zinc-500">{t.lessons_done}/{t.lessons_total} lessons · {t.progress_pct}%</span>
                    <span className="text-xs font-semibold text-red-600 inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                      Open track <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="mt-10 card-tech p-6">
          <div className="overline mb-2"><span className="red-bar" />How tracks work</div>
          <ul className="text-sm text-zinc-700 space-y-2 leading-relaxed">
            <li><strong>Modular by design.</strong> Take any track in any order. Skip tracks that don't apply to your career path.</li>
            <li><strong>Each track issues its own credential.</strong> Pass the track's final exam (70%+) to unlock the printable + QR-verified Completion Certificate.</li>
            <li><strong>All included.</strong> No additional purchase. Your $149 enrollment unlocks every track, plus the main NICET L1+L2+L3 curriculum, S-98 review, lab, and AI tutor.</li>
            <li><strong>Tracks complement, don't replace, factory certification.</strong> The Manufacturer Exposure track gives you operational familiarity with EST3 / Siemens / Notifier — factory-authorized credentials still require attending manufacturer-led training.</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
